/**
 * Mission page content — 100% fidelity to content/company/mission.md
 * Cross-reference: content/company/mission.md
 * 20 sections, all handcrafted from source.
 */

export const missionMeta = {
  title: "Our Mission | Perception X2 Vision",
  description:
    "Discover the mission behind CryptoMize and Perception X2. Autonomous perception amplification for every organization that needs it. Measurable impact. Ethical deployment.",
  slug: "mission",
  canonical: "https://perception.ac/mission",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  missionGlance: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Our Mission",
  title: "Autonomous Perception Amplification for",
  titleAccent: "Every Organization That Needs It.",
  lede: "CryptoMize exists to solve one problem: the gap between organizations that can engineer their digital perception and those that cannot. For over 15 years, we have built the technology, the expertise, and the operational infrastructure to close that gap.",
  body: "Our mission is not abstract. It is measured in 300-500% improvements in narrative penetration, 95-98% organic authenticity ratings, and 300+ organizations that trust Perception X2 to shape their perception at global scale.",
  cta: [
    { label: "Explore Our Mission", href: "#our-mission" },
    { label: "See Our Impact", href: "#impact" },
  ],
  missionGlance: [
    "Democratize Autonomous Perception Amplification",
    "Deliver Measurable, Verifiable Outcomes",
    "Maintain Ethical Deployment Standards",
    "Advance the Science of Perception Engineering",
  ],
};

export interface MissionStatementData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const missionStatement: MissionStatementData = {
  eyebrow: "Our Mission Statement",
  title: "The Mission That Drives Everything.",
  intro: "CryptoMize's mission is to make autonomous perception amplification accessible to every organization that needs it — from sovereign governments to growing enterprises. We believe that the ability to shape, protect, and amplify digital perception should not be reserved for the largest and most well-resourced entities alone.",
  pillars: [
    {
      title: "Why This Mission Exists",
      body: "The digital landscape has fundamentally changed how organizations are perceived. Search results, social mentions, news coverage, and content ecosystems shape stakeholder perception in real time. Organizations that fail to actively manage this perception lose competitive advantage to those who do. The problem: the tools required to engineer perception effectively have historically been accessible only to the largest organizations with the deepest resources.",
    },
    {
      title: "How We Fulfill This Mission",
      body: "CryptoMize builds and operates Perception X2 — the world's most advanced autonomous perception amplification platform. Through nine proprietary AI engines, 50+ platform coverage, and measurable outcomes, we deliver the capabilities that organizations need to control their perception at global scale.",
    },
    {
      title: "What This Mission Means",
      body: "For governments, it means national narrative management and public trust. For enterprises, it means brand protection and competitive positioning. For public figures, it means privacy and legacy management. For every organization we serve, it means the perception advantage that determines outcomes.",
    },
  ],
  cta: [
    { label: "Learn Our Vision", href: "#vision" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ProblemData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  intro?: string;
}

export const problem: ProblemData = {
  eyebrow: "The Problem We Solve",
  title: "Perception Determines Outcome. Most Organizations Cannot Engineer It.",
  intro: "",
  pillars: [
    {
      title: "The Perception Gap",
      body: "The core problem CryptoMize exists to solve is simple: perception determines outcomes, but most organizations lack the capability to engineer their perception effectively. A digital divide exists between organizations that can shape their perception and those that cannot.",
    },
    {
      title: "The Technology Gap",
      body: "The largest enterprises and most well-resourced governments employ teams of specialists, deploy sophisticated tools, and invest heavily in perception management. Smaller organizations, growing enterprises, and resource-constrained entities are left to react to whatever perception the digital landscape produces organically. Effective perception amplification requires technology that most organizations cannot build, buy, or operate.",
    },
    {
      title: "The Knowledge Gap",
      body: "Even with the right technology, effective perception amplification requires expertise in AI-driven narrative engineering, cognitive resonance mapping, and cross-platform deployment. This expertise is rare, expensive, and difficult to scale. CryptoMize bridges this knowledge gap through our strategic team and autonomous platform capabilities.",
    },
  ],
  cta: [
    { label: "See How We Solve This", href: "/about" },
    { label: "Request a Demo", href: "/demo" },
  ],
};

export interface VisionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const vision: VisionData = {
  eyebrow: "Our Vision",
  title: "The Future We Are Building.",
  intro: "CryptoMize envisions a future where every organization — regardless of size, resources, or geography — has access to the perception amplification capabilities that determine outcomes in the digital age.",
  pillars: [
    {
      title: "A World of Equal Perception Capability",
      body: "Today, perception amplification is a privilege of scale. The largest organizations can afford to hire specialists, deploy tools, and invest in the technology required to shape their digital presence. Smaller organizations cannot. CryptoMize envisions a future where this gap closes — where autonomous AI-driven platforms make perception amplification accessible to every organization that needs it.",
    },
    {
      title: "Autonomous by Default",
      body: "The future we are building is one where perception amplification operates autonomously. Not with less human involvement — with more strategic human involvement. AI handles the scale, speed, and complexity. Humans provide the strategy, ethics, and oversight. The result: better outcomes, faster response, and more effective perception management.",
    },
    {
      title: "Measurably Better",
      body: "Every outcome in our vision is measured. No claims without data. No promises without proof. The future of perception amplification is one where every deployment is tracked, every result is quantified, and every improvement is documented.",
    },
  ],
  cta: [
    { label: "Explore Our Roadmap", href: "#roadmap" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface GoalsData {
  eyebrow: string;
  title: string;
  intro: string;
  goals: { goal: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const goals: GoalsData = {
  eyebrow: "Our Goals",
  title: "What We Are Working Toward.",
  intro: "CryptoMize has defined specific, measurable goals that guide our work and measure our progress against our mission.",
  goals: [
    {
      goal: "Goal 1",
      title: "Platform Excellence",
      body: "Maintain and advance Perception X2 as the world's most advanced autonomous perception amplification platform. This means 99.9999% uptime, 300-500% narrative penetration improvement, and 95-98% organic authenticity — consistently, reliably, measurably.",
    },
    {
      goal: "Goal 2",
      title: "Global Accessibility",
      body: "Expand platform availability to serve organizations in every geography, every industry, and every use case. This means 100+ language support, multi-continent infrastructure, and deployment pathways for organizations of all sizes.",
    },
    {
      goal: "Goal 3",
      title: "Ethical Leadership",
      body: "Set the standard for ethical deployment of autonomous perception amplification technology. This means rigorous ethical review, transparent practices, and commitment to responsible innovation.",
    },
    {
      goal: "Goal 4",
      title: "Industry Advancement",
      body: "Advance the science and practice of perception engineering through research, thought leadership, and standards development. This means contributing to the broader ecosystem, not just our own platform.",
    },
  ],
  cta: [
    { label: "See Our Impact", href: "#impact" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ImpactData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; stats: string[] }[];
  cta: { label: string; href: string }[];
}

export const impact: ImpactData = {
  eyebrow: "Impact",
  title: "Measurable Outcomes. Proven Results.",
  intro: "CryptoMize measures impact obsessively. Every strategy, every deployment, and every client engagement is tracked, quantified, and reported. We do not claim impact — we prove it.",
  pillars: [
    {
      title: "Platform Impact",
      stats: [
        "300-500% improvement in narrative penetration",
        "95-98% organic authenticity ratings",
        "99.9999% uptime — approximately 31.5 seconds of downtime per year",
        "50+ platform coverage",
        "100+ language support",
        "Millions of data points processed daily",
      ],
    },
    {
      title: "Client Impact",
      stats: [
        "300+ elite organizations served",
        "Measurable improvements in search dominance",
        "Demonstrable narrative control across platforms",
        "Enhanced reputation resilience",
        "Competitive positioning advantages",
      ],
    },
    {
      title: "Industry Impact",
      stats: [
        "Setting the standard for autonomous perception amplification",
        "Advancing ethical deployment practices",
        "Contributing to industry standards and best practices",
        "Thought leadership across technology and communications",
      ],
    },
  ],
  cta: [
    { label: "View Our Results", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface DeliverData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const howWeDeliver: DeliverData = {
  eyebrow: "How We Deliver",
  title: "The CryptoMize Approach.",
  intro: "CryptoMize delivers on our mission through a disciplined approach that combines proprietary technology, strategic expertise, and operational excellence.",
  pillars: [
    { title: "Proprietary Technology", body: "Perception X2's nine AI engines are purpose-built for autonomous perception amplification. No off-the-shelf technology replicates what we have built. This technology is the foundation of everything we deliver." },
    { title: "Strategic Expertise", body: "Our strategic team translates platform capability into client outcomes. Every strategy is tailored to organizational objectives, market dynamics, and competitive positioning. Templates do not deliver results — custom strategies do." },
    { title: "Operational Excellence", body: "99.9999% uptime. Zero security incidents. Real-time monitoring. Automatic failover. Our operational infrastructure ensures consistent delivery across 300+ clients and 50+ platforms." },
    { title: "Measurable Outcomes", body: "Every engagement produces measurable results. 300-500% improvement in narrative penetration. 95-98% organic authenticity. These are not aspirations — they are standards." },
  ],
  cta: [
    { label: "Explore Our Platform", href: "/about" },
    { label: "Request a Demo", href: "/demo" },
  ],
};

export interface EthicsData {
  eyebrow: string;
  title: string;
  intro: string;
  principles: { name: string; body: string }[];
  review: { title: string; body: string };
  commitment: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const ethicalFramework: EthicsData = {
  eyebrow: "Ethical Framework",
  title: "How We Deploy Our Technology.",
  intro: "CryptoMize is committed to the ethical deployment of autonomous perception amplification technology. Our ethical framework governs every capability we develop, every strategy we deploy, and every outcome we deliver.",
  principles: [
    { name: "Legitimacy", body: "We serve organizations with legitimate perception management needs" },
    { name: "Transparency", body: "We are honest about what our technology can and cannot do" },
    { name: "Compliance", body: "We operate within all applicable laws and regulations" },
    { name: "Accountability", body: "We take responsibility for the outcomes our technology produces" },
    { name: "Restraint", body: "We decline engagements that conflict with our ethical standards" },
  ],
  review: {
    title: "Review Process",
    body: "Every new capability undergoes ethical review before deployment. The review evaluates potential impact, regulatory considerations, and alignment with our principles. Capabilities that fail review are not deployed — regardless of commercial potential.",
  },
  commitment: {
    title: "Ongoing Commitment",
    body: "Ethics at CryptoMize is not a one-time assessment — it is an ongoing commitment. We continuously evaluate our technology, our deployments, and our outcomes against our ethical framework.",
  },
  cta: [
    { label: "Our Ethics", href: "/leadership" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface TechMissionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const technologyMission: TechMissionData = {
  eyebrow: "The Technology Mission",
  title: "Advancing Autonomous Perception Amplification.",
  intro: "CryptoMize's technology mission is to advance the state of the art in autonomous perception amplification through continuous innovation, rigorous engineering, and applied research.",
  pillars: [
    { title: "Current Capabilities", body: "Perception X2's nine AI engines represent the most advanced autonomous perception amplification technology in existence. From organic simulation to cognitive resonance mapping to echo chamber architecture, each engine addresses a specific dimension of perception management with proprietary technology." },
    { title: "Innovation Roadmap", body: "Our technology roadmap targets improvements across all nine AI engines, expanded platform coverage, deeper integration capabilities, and enhanced autonomous operations. Every research initiative targets a specific capability improvement or market need." },
    { title: "Applied Research", body: "CryptoMize invests in applied research across artificial intelligence, cognitive science, and perception engineering. This research is not academic — it is designed to produce tangible improvements in platform performance and client outcomes." },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface CommitmentData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const clientCommitment: CommitmentData = {
  eyebrow: "Our Commitment to Clients",
  title: "What Every Client Can Expect.",
  intro: "CryptoMize commits to every client relationship with three non-negotiable guarantees: measurable outcomes, operational transparency, and strategic partnership.",
  pillars: [
    { title: "Measurable Outcomes", body: "Every client engagement produces quantifiable results. We establish baseline metrics at the start of every engagement and track improvement continuously. 300-500% improvement in narrative penetration is our standard — not our aspiration." },
    { title: "Operational Transparency", body: "Every client has real-time visibility into platform performance, deployment status, and outcome metrics. We publish our status openly. We report incidents honestly. We provide data that clients can verify independently." },
    { title: "Strategic Partnership", body: "Every client engagement is led by a senior strategist who understands their objectives, their market, and their competitive landscape. We do not assign junior staff to manage critical relationships." },
  ],
  cta: [
    { label: "Request a Demo", href: "/demo" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface SecurityCommData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const securityCommitment: SecurityCommData = {
  eyebrow: "Our Commitment to Security",
  title: "Protecting What Matters.",
  intro: "CryptoMize serves organizations where security is not optional — it is existential. Our commitment to security is absolute.",
  pillars: [
    { title: "Infrastructure Security", body: "Government-grade encryption. Multi-layer access controls. Continuous threat monitoring. Our infrastructure meets or exceeds the security standards required by the most security-conscious organizations on earth." },
    { title: "Data Protection", body: "Client data is encrypted at rest and in transit. Access is strictly controlled. Auditing is continuous. We maintain zero security incidents across 15+ years of operations — a record we protect aggressively." },
    { title: "Operational Security", body: "Every team member undergoes security clearance. Every engagement operates under strict NDA. Every system is hardened against intrusion. Security is not a department at CryptoMize — it is a culture." },
  ],
  cta: [
    { label: "Our Security", href: "/about#technology" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface IndustryImpactData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const industryImpactMission: IndustryImpactData = {
  eyebrow: "Industry Impact",
  title: "Raising the Bar for the Entire Industry.",
  intro: "CryptoMize does not just participate in the perception management industry — we define it. Our innovations, standards, and thought leadership shape the conversation for the entire sector.",
  pillars: [
    { title: "Setting Standards", body: "CryptoMize has contributed to the development of industry standards for perception management, AI ethics, and digital intelligence. We believe in raising the bar for everyone." },
    { title: "Thought Leadership", body: "Our executives contribute to industry publications, present at global conferences, and brief industry analysts. Our insights on autonomous perception amplification inform the broader conversation." },
    { title: "Ethical Leadership", body: "We advocate for responsible deployment of perception amplification technology. Our ethical framework is designed to serve as a model for the industry." },
  ],
  cta: [
    { label: "Read Our Insights", href: "/press" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface PeopleData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const people: PeopleData = {
  eyebrow: "Our People",
  title: "The Team Behind the Mission.",
  intro: "CryptoMize's mission is fulfilled by a team of specialists who share a common conviction: perception determines outcomes, and we can engineer those outcomes measurably, ethically, and at scale.",
  pillars: [
    { title: "Who We Recruit", body: "We recruit exceptional minds with deep expertise in AI engineering, cognitive science, strategic communications, and operational excellence. We hire for capability and character — not just credentials." },
    { title: "How We Develop", body: "Every team member invests in continuous learning. CryptoMize provides the resources, challenges, and support that enable our people to grow alongside the platform they build and operate." },
    { title: "What Drives Us", body: "Our people are driven by the difficulty of the problems we solve, the advancement of the technology we build, and the measurable impact of the outcomes we deliver. This is not a comfortable place to coast — it is an exceptional place to grow." },
  ],
  cta: [
    { label: "Explore Careers", href: "/careers" },
    { label: "Join Our Team", href: "/contact" },
  ],
};

export interface CommunityData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const community: CommunityData = {
  eyebrow: "Community Responsibility",
  title: "Our Obligation Beyond Business.",
  intro: "CryptoMize recognizes that autonomous perception amplification technology carries responsibilities beyond commercial outcomes. We take those responsibilities seriously.",
  pillars: [
    { title: "Responsible Deployment", body: "We deploy our technology only for legitimate purposes. We decline engagements that conflict with our ethical standards, regardless of commercial value." },
    { title: "Industry Contribution", body: "We contribute to the perception management industry through standards development, research publication, and thought leadership. Our goal is to advance the entire industry — not just our platform." },
    { title: "Digital Ecosystem Health", body: "We consider the impact of our technology on the broader digital ecosystem. Our platform is designed to enhance, not degrade, the quality of digital information." },
  ],
  cta: [
    { label: "Our Ethics", href: "/leadership" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface LongTermVisionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const longTermVision: LongTermVisionData = {
  eyebrow: "Long-Term Vision",
  title: "Where We Are Going.",
  intro: "CryptoMize's long-term vision extends beyond the current state of perception management. We are building toward a future where autonomous perception amplification is a standard capability for every organization.",
  pillars: [
    { title: "Next-Generation AI", body: "We are developing next-generation AI capabilities that will enhance Perception X2's performance across all dimensions — from emotional resonance mapping to autonomous deployment to predictive intelligence." },
    { title: "Global Expansion", body: "We are expanding our operations to serve new industries, geographies, and use cases. Every expansion is guided by our mission: make autonomous perception amplification accessible to every organization that needs it." },
    { title: "Industry Evolution", body: "We are investing in the evolution of the perception management industry — through research, standards development, and ethical leadership. Our goal is to shape the future, not just adapt to it." },
  ],
  cta: [
    { label: "Explore Our Roadmap", href: "/history" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface MetricsData {
  eyebrow: string;
  title: string;
  intro: string;
  categories: { title: string; stats: string[] }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const successMetrics: MetricsData = {
  eyebrow: "Success Metrics",
  title: "How We Measure Our Mission.",
  intro: "CryptoMize measures mission fulfillment through specific, quantifiable metrics that track our progress against our goals.",
  categories: [
    {
      title: "Platform Performance",
      stats: [
        "Uptime: 99.9999%",
        "Narrative penetration improvement: 300-500%",
        "Organic authenticity: 95-98%",
        "Platform coverage: 50+ platforms",
        "Language support: 100+ languages",
      ],
    },
    {
      title: "Client Outcomes",
      stats: [
        "Organizations served: 300+",
        "Client satisfaction: measured quarterly",
        "Retention rate: measured annually",
        "Outcome verification: continuous",
      ],
    },
    {
      title: "Ethical Compliance",
      stats: [
        "Security incidents: zero",
        "Ethical reviews completed: 100%",
        "Regulatory compliance: full",
        "Transparency score: independently verified",
      ],
    },
  ],
  cta: [
    { label: "View Our Results", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface PartnershipData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const partnership: PartnershipData = {
  eyebrow: "Partnership in Mission",
  title: "We Do Not Do This Alone.",
  intro: "CryptoMize's mission is advanced through strategic partnerships with technology providers, advisors, and channel organizations.",
  pillars: [
    { title: "Technology Partners", body: "Technology partners extend Perception X2's capabilities through native integrations, platform connectivity, and co-development. Together, we deliver outcomes that neither organization could achieve alone." },
    { title: "Strategic Advisors", body: "Strategic advisors provide market intelligence, industry expertise, and relationship networks that enhance CryptoMize's ability to serve clients across industries and geographies." },
    { title: "Channel Partners", body: "Channel partners extend our reach into new markets, industries, and geographies. Through their local expertise and market relationships, they help us fulfill our mission of making autonomous perception amplification accessible to every organization that needs it." },
  ],
  cta: [
    { label: "Explore Partnerships", href: "/partners" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface GlobalCommData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const globalCommitment: GlobalCommData = {
  eyebrow: "Global Commitment",
  title: "Serving Every Market. Respecting Every Culture.",
  intro: "CryptoMize's mission extends across every geography, every industry, and every culture we serve. Our commitment to global service is matched by our commitment to local respect.",
  pillars: [
    { title: "Cultural Intelligence", body: "Perception is shaped by culture, language, and context. CryptoMize invests in cultural intelligence to ensure that every strategy we deploy resonates authentically in its target market. 100+ language support means nothing without the cultural context to deploy it correctly." },
    { title: "Regulatory Respect", body: "Every market operates under unique regulations. CryptoMize maintains compliance frameworks that ensure our deployments meet local requirements without exception. We do not treat regulation as an obstacle — we treat it as a standard." },
    { title: "Local Impact, Global Standards", body: "Our mission delivers local impact while maintaining global standards. The same 300-500% improvement in narrative penetration and 95-98% organic authenticity is achieved in every market we serve — adapted for local context, validated by local outcomes." },
  ],
  cta: [
    { label: "Explore Our Operations", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface SustainableData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const sustainable: SustainableData = {
  eyebrow: "Sustainable Mission",
  title: "Building for the Long Term.",
  intro: "CryptoMize's mission is designed for sustainability — not just immediate outcomes, but enduring impact.",
  pillars: [
    { title: "Platform Durability", body: "Perception X2 is built on proprietary technology that evolves with the market. Continuous innovation ensures that our platform remains state of the art — not just today, but for years to come." },
    { title: "Relationship Depth", body: "We build relationships that deepen over time. Every client engagement is designed for long-term partnership, not short-term transaction. The result: 300+ clients who trust CryptoMize because we deliver consistently." },
    { title: "Ethical Sustainability", body: "Our ethical framework ensures that our mission remains aligned with responsible innovation. We do not sacrifice long-term integrity for short-term gain. Every capability we develop, every strategy we deploy, and every outcome we deliver passes the test of ethical sustainability." },
  ],
  cta: [
    { label: "Our Ethics", href: "/leadership" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  steps: { step: string; title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const missionCta: CtaData = {
  eyebrow: "Get Started",
  title: "Join Us in Fulfilling This Mission.",
  intro: "CryptoMize invites you to discover what autonomous perception amplification can achieve for your organization. Whether you are a government agency, a global enterprise, a growing business, or a public figure, Perception X2 delivers the outcomes that determine your perception — and your success.",
  steps: [
    { step: "1", title: "Request a Demo", body: "See Perception X2 in action with a personalized demonstration" },
    { step: "2", title: "Book a Consultation", body: "Speak with a senior specialist about your objectives" },
    { step: "3", title: "Become a Partner", body: "Extend your capabilities through the Perception X2 ecosystem" },
    { step: "4", title: "Join Our Team", body: "Build what others cannot with a team of elite specialists" },
  ],
  cta: [
    { label: "Request a Demo", href: "/demo" },
    { label: "Book a Consultation", href: "/contact" },
    { label: "Contact Us", href: "/contact" },
  ],
};
